import { AppUrls } from '@/urls';
import React, { useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { apiSdk } from '@/httpClient';
import FormItem from '@/components/Form/FormItem';
import { UNKNOWN_ERROR_MESSAGE } from '@/config';
import Spin from '@/components/Spin';
import { FormValidation } from '@/helpers/formValidations';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import PasswordInput from '@/components/Form/PasswordInput';

export default function ResetPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = React.useState<0 | 1 | 2>(0); //0:loading,1:invalid,2 valid
  const { token } = useParams<{ token: string }>();

  const history = useHistory();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  React.useEffect(() => {
    setTokenStatus(0);
    apiSdk.AgentUserService.checkResetPasswordToken({
      token,
    })
      .then(res => {
        if (res) {
          setTokenStatus(2);
        } else {
          setTokenStatus(1);
        }
      })
      .catch(() => {
        setTokenStatus(1);
      });
  }, [token]);

  const forgotPassword = (values: any) => {
    setLoading(true);
    apiSdk.AgentUserService.resetPassword({
      body: {
        token: token,
        newPassword: values.password,
      },
    })
      .then(res => {
        if (res.success) {
          ModalUtils.success({
            content: t('resetPasswordPage_ResetPasswordSuccess'),
            onClose: () => {
              history.push(AppUrls.Login);
            },
          });
        } else {
          ModalUtils.error({
            content: t(res.message || UNKNOWN_ERROR_MESSAGE),
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <section className="layout-pt-lg layout-pb-lg bg-blue-2">
      <Helmet>
        <title>Reset password</title>
      </Helmet>
      <div className="container">
        <div className="row justify-center">
          <div className="col-xl-6 col-lg-7 col-md-9">
            <Spin loading={loading || tokenStatus === 0}>
              <div className="px-50 py-50 sm:px-20 sm:py-20 bg-white shadow-4 rounded-4">
                {tokenStatus === 1 ? (
                  <div style={{ textAlign: 'center', padding: 50 }}>
                    <div style={{ textAlign: 'center', fontSize: 20, color: '#f44336', marginBottom: 20 }}>
                      {t('resetPasswordPage_ForgotPasswordSessionExpired')}
                    </div>
                    <Link to={AppUrls.Login} className="text-blue-1">
                      {t('base.BackLoginPage')}
                    </Link>
                  </div>
                ) : (
                  <form className="row y-gap-20" onSubmit={handleSubmit(forgotPassword)}>
                    <div className="col-12">
                      <h1 className="text-22 fw-500">{t('resetPasswordPage_TitleResetPassword')} </h1>
                      <p className="mt-10">{t('resetPasswordPage_SubTitleResetPassword')}</p>
                    </div>

                    <div className="col-12">
                      <FormItem errors={errors} label={t('resetPasswordPage_NewPassword')} name="password">
                        <PasswordInput
                          {...register('password', { ...FormValidation.Required, ...FormValidation.Password })}
                          autoComplete="new-password"
                        />
                      </FormItem>
                    </div>

                    <div className="col-12">
                      <FormItem errors={errors} label={t('resetPasswordPage_ConfirmPassword')} name="confirmPassword">
                        <PasswordInput
                          {...register('confirmPassword', {
                            validate: (confirmPassword: string) => {
                              if (watch('password') !== confirmPassword) {
                                return t('resetPasswordPage_PasswordNotMatch') as any;
                              }
                              return undefined;
                            },
                          })}
                        />
                      </FormItem>
                    </div>
                    <div className="italic text-light-1">
                      <div dangerouslySetInnerHTML={{ __html: t('PASS_WORD_HINT_TEXT') as any }} />
                    </div>
                    <div className="col-12">
                      <Link to={AppUrls.Login} className="text-blue-1">
                        {t('base.BackLoginPage')}
                      </Link>
                    </div>

                    <div className="col-12">
                      <button type="submit" className="button py-20 -dark-1 bg-blue-1 text-white w-100">
                        {t('passwordExpiredPage_ChangePassword')} <div className="icon-arrow-top-right ml-15" />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Spin>
          </div>
        </div>
      </div>
    </section>
  );
}
